'use client';

/**
 * Privacy settings helpers — persists user privacy preferences in localStorage
 * so they can be read synchronously by analytics and other modules.
 *
 * Keys are scoped per user to avoid leaking settings across accounts.
 */

const KEY_AI_PROCESSING = (uid: string) => `uyir_ai_processing_${uid}`;
const KEY_ENCRYPTED_BACKUP = (uid: string) => `uyir_encrypted_backup_${uid}`;
const KEY_ANALYTICS_OPT_OUT = (uid: string) => `uyir_analytics_opt_out_${uid}`;

// ── Persist ────────────────────────────────────────────────────────────────────

export function persistPrivacySettings(
  uid: string,
  settings: {
    aiProcessing: boolean;
    encryptedBackup: boolean;
    analyticsOptOut: boolean;
  }
): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(KEY_AI_PROCESSING(uid), String(settings.aiProcessing));
    localStorage.setItem(KEY_ENCRYPTED_BACKUP(uid), String(settings.encryptedBackup));
    localStorage.setItem(KEY_ANALYTICS_OPT_OUT(uid), String(settings.analyticsOptOut));
  } catch {}
}

// ── Read ───────────────────────────────────────────────────────────────────────

/** Returns true if AI processing is allowed (default: true). */
export function isAIProcessingEnabled(uid: string): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const val = localStorage.getItem(KEY_AI_PROCESSING(uid));
    return val === null ? true : val === 'true';
  } catch {
    return true;
  }
}

/** Returns true if encrypted cloud backup is enabled (default: true). */
export function isEncryptedBackupEnabled(uid: string): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const val = localStorage.getItem(KEY_ENCRYPTED_BACKUP(uid));
    return val === null ? true : val === 'true';
  } catch {
    return true;
  }
}

/** Returns true if the user has opted OUT of analytics (default: false). */
export function isAnalyticsOptedOut(uid?: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    // Global opt-out key (set without uid for anonymous/pre-login state)
    const globalVal = localStorage.getItem('uyir_analytics_opt_out');
    if (globalVal === 'true') return true;
    if (!uid) return false;
    const val = localStorage.getItem(KEY_ANALYTICS_OPT_OUT(uid));
    return val === 'true';
  } catch {
    return false;
  }
}

/** Persist analytics opt-out globally (also works before login). */
export function setAnalyticsOptOut(optOut: boolean, uid?: string): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem('uyir_analytics_opt_out', String(optOut));
    if (uid) {
      localStorage.setItem(KEY_ANALYTICS_OPT_OUT(uid), String(optOut));
    }
  } catch {}
}
